import type {
  CheckInTemplate,
  Tag,
  Timestamp,
  Track,
  ValidationError,
} from '@anytracker/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { useToast } from '../../components/Toast.js'
import { ValueInput } from '../../components/value-input/ValueInput.js'
import { useDeclareUnsaved } from '../../pwa/UnsavedWork.js'
import { useServices } from '../../services/ServicesProvider.js'
import { TagPicker } from './TagPicker.js'
import { WhenControl } from './WhenControl.js'
import { prune, useDraft, type DraftState } from './useDraft.js'
import styles from './EntryForm.module.css'

/**
 * Recording a measurement (T-304).
 *
 * The most important screen in the application: everything else operates on
 * what this captures, and adherence is the product requirement. Logging that
 * takes six taps and a scroll stops happening in week two, and the resulting
 * gaps are indistinguishable from real signal (ADR-0005).
 *
 * Two rules run through the whole file:
 *
 *   - **Skipping means absent, never zero.** An unset track records no
 *     measurement. A missing satiety reading and a satiety reading of zero are
 *     entirely different claims.
 *   - **The form never branches on track kind.** `ValueInput` dispatches, so
 *     a fourth value type is a change in one place rather than here.
 */
export function EntryFormScreen() {
  const { repos, clock, storage } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()
  const params = useParams()
  const templateId = params['templateId'] ?? null

  const [template, setTemplate] = useState<CheckInTemplate | null>(null)
  const [tracks, setTracks] = useState<Track[]>([])
  const [missing, setMissing] = useState<string[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [loaded, setLoaded] = useState(false)
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [openNotes, setOpenNotes] = useState<ReadonlySet<string>>(new Set())
  const [saving, setSaving] = useState(false)

  const initial = useCallback(
    (): DraftState => ({
      at: clock.now(),
      tagIds: [],
      note: '',
      values: {},
      slotNotes: {},
    }),
    [clock],
  )

  const { draft, update, setValue, clear, restored } = useDraft(
    storage,
    templateId,
    initial,
  )

  const load = useCallback(async () => {
    const [allTracks, allTags] = await Promise.all([
      repos.tracks.list({ includeArchived: true }),
      repos.tags.list(),
    ])
    setTags(allTags)

    if (templateId === null) {
      // Ad hoc: every active track, in the user's own order.
      setTracks(allTracks.filter((track) => !track.archived))
      setLoaded(true)
      return
    }

    const found = await repos.templates.get(templateId)
    if (!found) {
      setLoaded(true)
      return
    }
    setTemplate(found)

    const byId = new Map(allTracks.map((track) => [track.id, track]))
    const resolved: Track[] = []
    const absent: string[] = []
    // Template order, not track order — the user arranged these deliberately.
    for (const trackId of found.trackIds) {
      const track = byId.get(trackId)
      if (track === undefined) {
        absent.push(trackId)
      } else if (!track.archived) {
        // An archived track is dropped from the form rather than shown greyed
        // out: it cannot be logged against usefully, and a field that exists
        // only to be ignored is worse than one that is not there.
        resolved.push(track)
      }
    }
    setTracks(resolved)
    setMissing(absent)
    setLoaded(true)
  }, [repos, templateId])

  useEffect(() => {
    void load()
  }, [load])

  // The template's default tags, applied once when the form opens fresh.
  useEffect(() => {
    if (template && draft && !restored && draft.tagIds.length === 0) {
      update({ tagIds: [...template.defaultTagIds] })
    }
    // Only when the template first resolves; re-running would fight the user
    // deselecting a default tag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, restored])

  const filled = useMemo(
    () => (draft ? Object.keys(prune(draft.values, tracks)).length : 0),
    [draft, tracks],
  )

  // A part-filled form is unsaved work: the update prompt must not reload over
  // it (T-204).
  useDeclareUnsaved(filled > 0)

  async function createTag(name: string) {
    const result = await repos.tags.create(name)
    if (!result.ok) {
      show({
        message: result.errors[0]?.message ?? 'Could not add that.',
        tone: 'danger',
      })
      return
    }
    setTags(await repos.tags.list())
    if (draft) {
      update({ tagIds: [...draft.tagIds, result.value.id] })
    }
  }

  async function save() {
    if (!draft || saving) {
      return
    }
    setErrors([])
    setSaving(true)

    const measurements = tracks
      .filter((track) => draft.values[track.id] !== undefined)
      .map((track) => {
        const note = draft.slotNotes[track.id]?.trim()
        return {
          trackId: track.id,
          value: draft.values[track.id]!,
          ...(note === undefined || note === '' ? {} : { note }),
        }
      })

    const result = await repos.entries.create({
      at: draft.at,
      tagIds: draft.tagIds,
      measurements,
      ...(templateId === null ? {} : { templateId }),
      ...(draft.note.trim() === '' ? {} : { note: draft.note.trim() }),
    })

    setSaving(false)
    if (!result.ok) {
      // Every failure at once, not one at a time — core reports them together
      // and re-submitting to discover the next one is its own kind of friction.
      setErrors(result.errors)
      return
    }

    await clear()
    show({ message: 'Recorded.' })
    void navigate('/')
  }

  if (!loaded || !draft) {
    return (
      <>
        <h1>Log</h1>
        <p>Loading…</p>
      </>
    )
  }

  const title = template?.name ?? 'Log anything'

  return (
    <>
      <h1>{title}</h1>

      <div className={styles['form']}>
        {restored && filled > 0 && (
          <p className={styles['notice']}>
            Picking up where you left off — nothing was lost.
          </p>
        )}

        {missing.length > 0 && (
          <p className={styles['notice']}>
            {missing.length === 1
              ? 'One track in this check-in no longer exists'
              : `${String(missing.length)} tracks in this check-in no longer exist`}{' '}
            and cannot be asked for. The rest still work — you can tidy it up in Check-ins
            whenever suits.
          </p>
        )}

        <WhenControl
          at={draft.at}
          now={clock.now()}
          onChange={(at: Timestamp) => update({ at })}
        />

        {tracks.length === 0 ? (
          <p className={styles['notice']}>
            There are no tracks to record. Set one up first.
          </p>
        ) : (
          tracks.map((track) => {
            const value = draft.values[track.id]
            const noteOpen = openNotes.has(track.id)
            return (
              <section key={track.id} className={styles['slot']}>
                <div className={styles['slotHeader']}>
                  <span className={styles['slotName']} id={`label-${track.id}`}>
                    {track.name}
                  </span>
                  {track.unit !== undefined && (
                    <span className={styles['slotUnit']}>{track.unit}</span>
                  )}
                </div>
                {track.description !== undefined && (
                  <p className={styles['slotDescription']}>{track.description}</p>
                )}

                {/* Polymorphic: this form never asks what kind of track it is. */}
                <ValueInput
                  track={track}
                  value={value ?? null}
                  labelledBy={`label-${track.id}`}
                  onChange={(next) => setValue(track.id, next ?? undefined)}
                />

                {value !== undefined && (
                  <button
                    type="button"
                    className={styles['skip']}
                    onClick={() => setValue(track.id, undefined)}
                  >
                    Skip {track.name} — record nothing for it
                  </button>
                )}

                {noteOpen ? (
                  <textarea
                    className={styles['slotNote']}
                    aria-label={`Note about ${track.name}`}
                    value={draft.slotNotes[track.id] ?? ''}
                    placeholder="Anything worth remembering about this one"
                    onChange={(event) =>
                      update({
                        slotNotes: {
                          ...draft.slotNotes,
                          [track.id]: event.target.value,
                        },
                      })
                    }
                  />
                ) : (
                  // Collapsed by default, so it costs nothing when unused —
                  // which is almost always.
                  <button
                    type="button"
                    className={styles['noteToggle']}
                    onClick={() => setOpenNotes(new Set([...openNotes, track.id]))}
                  >
                    Add a note
                  </button>
                )}
              </section>
            )
          })
        )}

        <section className={styles['section']}>
          <h2 className={styles['sectionTitle']}>Tags</h2>
          <TagPicker
            tags={tags}
            selected={draft.tagIds}
            onToggle={(tagId) =>
              update({
                tagIds: draft.tagIds.includes(tagId)
                  ? draft.tagIds.filter((id) => id !== tagId)
                  : [...draft.tagIds, tagId],
              })
            }
            onCreate={createTag}
          />
        </section>

        <section className={styles['section']}>
          <h2 className={styles['sectionTitle']}>Note</h2>
          <textarea
            className={styles['note']}
            aria-label="Note about this entry"
            value={draft.note}
            placeholder="What was going on?"
            onChange={(event) => update({ note: event.target.value })}
          />
        </section>

        {errors.length > 0 && (
          <div className={styles['errors']} role="alert">
            {errors.map((error) => (
              <span key={error.code} className={styles['error']}>
                {error.message}
              </span>
            ))}
          </div>
        )}

        <div className={styles['actions']}>
          <Button
            onClick={() => {
              void clear().then(() => navigate('/'))
            }}
          >
            Discard
          </Button>
          <Button
            variant="primary"
            fullWidth
            disabled={filled === 0 || saving}
            onClick={() => void save()}
          >
            {filled === 0
              ? 'Nothing to save yet'
              : `Save ${filled === 1 ? '1 measurement' : `${String(filled)} measurements`}`}
          </Button>
        </div>
      </div>
    </>
  )
}
